import { type FormEvent } from 'react';
import { Loader2, Zap } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useDiagnosticStore } from '../../../stores/diagnostic';
import { apiFetch, ApiError } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { Eyebrow } from './types';

interface Step3QuestionsProps {
  onNext: () => void;
}

type DiagAnswer = { questionId: string; value: unknown };
type DiagQuestion = { id: string };

/** Une question est "répondue" si elle a une valeur non vide (cohérent avec answers.min(1) côté serveur). */
function isAnswered(question: DiagQuestion, answers: DiagAnswer[]): boolean {
  const a = answers.find((x) => x.questionId === question.id);
  if (!a) return false;
  const v = a.value;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') return v.trim().length > 0;
  return v != null;
}

export function Step3Questions({ onNext }: Step3QuestionsProps) {
  const store = useDiagnosticStore();

  const allAnswered =
    store.questions.length > 0 &&
    store.questions.every((q) => isAnswered(q as DiagQuestion, store.answers as DiagAnswer[]));

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!store.siret || !store.segment) return;
    if (!allAnswered) {
      store.setError('Merci de répondre à toutes les questions pour lancer le calcul.');
      return;
    }
    store.setError(null);
    store.setLoading(true);
    try {
      const result = await apiFetch<{
        primeMin: number;
        primeMax: number;
        fichesEligibles: { code: string; label: string; primeMin: number; primeMax: number }[];
        travauxRecommandes: string[];
      }>('/api/diagnostic/estimer', {
        json: {
          siret: store.siret,
          segment: store.segment,
          naf: store.naf ?? null,
          answers: store.answers,
        },
      });
      // Hydrate l'estimation AVANT de naviguer (sinon flash de l'étape Lead sans bandeau).
      store.setEstimation(result);
      onNext();
    } catch (err) {
      store.setError(err instanceof ApiError ? err.message : 'Erreur lors du calcul. Réessayez.');
    } finally {
      store.setLoading(false);
    }
  }

  return (
    <div>
      <Eyebrow>Étape 3 sur 5</Eyebrow>
      <h3 className="mt-2 h-section">
        Décrivez votre situation
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        {store.questions.length} question{store.questions.length > 1 ? 's' : ''} adaptée{store.questions.length > 1 ? 's' : ''} à votre profil et votre secteur.
      </p>

      {store.questions.length === 0 && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-[13px] leading-relaxed text-amber-900">
          Nous n&rsquo;avons pas pu charger les questions adaptées à votre secteur. Utilisez le
          bouton « Retour » pour vérifier votre SIRET, ou{' '}
          <a href="/contact" className="font-semibold underline underline-offset-2">
            contactez-nous directement
          </a>{' '}
          — un expert vous répondra sous 24 h ouvrées.
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-5">
        {store.questions.map((q) => (
          <QuestionField key={q.id} question={q} />
        ))}
        {store.questions.length > 0 && (
        <Button type="submit" variant="brand" size="xl" disabled={store.loading || !allAnswered} className="w-full gap-2">
          {store.loading ? (
            <><Loader2 className="h-4 w-4 animate-spin" />Calcul en cours…</>
          ) : (
            <>Calculer mon estimation<Zap className="h-4 w-4" /></>
          )}
        </Button>
        )}
        {!allAnswered && store.questions.length > 0 && (
          <p className="text-center text-[12px] text-stone-400">
            Répondez à toutes les questions pour lancer le calcul.
          </p>
        )}
      </form>
    </div>
  );
}

function QuestionField({
  question,
}: {
  question: {
    id: string;
    type: string;
    label: string;
    options?: { id: string; label: string }[];
    min?: number;
  };
}) {
  const setAnswer = useDiagnosticStore((s) => s.setAnswer);
  const stored = useDiagnosticStore((s) => s.answers.find((a) => a.questionId === question.id));

  if (question.type === 'multi-select' && question.options) {
    const selected = (stored?.value as string[] | undefined) ?? [];
    return (
      <fieldset className="space-y-2">
        <legend className="text-[13px] font-semibold text-stone-800">{question.label}</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {question.options.map((opt) => {
            const checked = selected.includes(opt.id);
            return (
              <label
                key={opt.id}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3.5 text-[13px] font-medium transition-all duration-150 ease-out',
                  checked
                    ? 'border-brand-400 bg-brand-50 text-brand-800'
                    : 'border-stone-200 text-stone-700 hover:border-stone-300 hover:bg-stone-50',
                )}
              >
                <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors', checked ? 'border-brand-500 bg-brand-500' : 'border-stone-300 bg-white')}>
                  {checked && (
                    <svg className="h-3 w-3 text-white" viewBox="0 0 12 10" fill="none">
                      <path d="M1 5l3.5 3.5L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <input type="checkbox" checked={checked} onChange={() => {
                  const next = checked ? selected.filter((s) => s !== opt.id) : [...selected, opt.id];
                  setAnswer({ questionId: question.id, value: next });
                }} className="sr-only" />
                {opt.label}
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (question.type === 'select' && question.options) {
    return (
      <div>
        <Label htmlFor={question.id} className="text-[13px] font-semibold">{question.label}</Label>
        <select
          id={question.id}
          className="mt-1.5 h-11 w-full rounded-xl border-2 border-stone-200 bg-white px-3 text-[13px] font-medium text-stone-800 transition-colors focus:border-brand-400 focus:outline-none"
          value={(stored?.value as string) ?? ''}
          onChange={(e) => setAnswer({ questionId: question.id, value: e.target.value })}
        >
          <option value="">— Choisir —</option>
          {question.options.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor={question.id} className="text-[13px] font-semibold">{question.label}</Label>
      <Input
        id={question.id}
        type={question.type === 'number' ? 'number' : 'text'}
        inputMode={question.type === 'number' ? 'numeric' : undefined}
        min={question.min}
        value={(stored?.value as string | number) ?? ''}
        onChange={(e) => setAnswer({ questionId: question.id, value: question.type === 'number' ? Number(e.target.value) : e.target.value })}
        className="mt-1.5 h-11 rounded-xl border-2 border-stone-200 focus:border-brand-400"
      />
    </div>
  );
}
